/**
 * Process HTML content: inject SalesRook header and footer around user-uploaded HTML.
 * User's dark/light theme and styling is preserved - we only add our header/footer.
 * Reference: server report-html-template.html
 * @param includeHeaderFooter - when false, header and footer are omitted from the final HTML
 */
export function processHtmlContent(
  content: string,
  shareUrl: string,
  title: string,
  description: string,
  includeHeaderFooter = true
): string {
  // SalesRook header - matches report-html-template.html structure
  const header = `  <header class="sr-artifact-header">
    <div class="sr-artifact-container">
      <div class="sr-artifact-logo-header">
        <img src="https://storage.googleapis.com/sr-widget/salesrook-logo-white.webp" alt="SalesRook Logo" class="sr-artifact-logo-image" />
      </div>
    </div>
  </header>`;

  // SalesRook footer - Talk to us + contact info (Talk to us replaced the NPS link, Oct 2026)
  const footer = `  <footer class="sr-artifact-footer">
    <div class="sr-artifact-container">
      <div class="sr-artifact-footer-content">
        <div class="sr-artifact-talk-section">
          <h3>Questions about this report?</h3>
          <p>Book thirty minutes with our customer success team to go through your numbers, or tell us about anything else on your account.</p>
          <a href="https://salesrook.com/account/talk-to-us" target="_blank" rel="noopener" class="sr-artifact-talk-link">Talk to us</a>
        </div>
        <div>
          <h3>Contact SalesRook</h3>
          <p><strong>Email:</strong> support@salesrook.com</p>
          <p><strong>Phone:</strong> +44 808 175 1795</p>
          <p><strong>Website:</strong> https://salesrook.com</p>
          <p style="margin-top: 20px;"><strong>Address:</strong><br />Unit A, Cottonworks House<br />111 Seven Sisters Road<br />London N7 7FN</p>
        </div>
      </div>
    </div>
  </footer>`;

  // Meta tags for WhatsApp/social sharing
  const metaTags = `
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${escapeHtml(title)}</title>
    <meta name="description" content="${escapeHtml(description)}">
    
    <!-- WhatsApp-optimized Open Graph Meta Tags -->
    <meta property="og:title" content="${escapeHtml(title)}">
    <meta property="og:description" content="${escapeHtml(description)}">
    <meta property="og:image" content="https://storage.googleapis.com/sr-widget/salesrook-image.jpeg">
    <meta property="og:image:width" content="1200">
    <meta property="og:image:height" content="630">
    <meta property="og:image:alt" content="SalesRook Report">
    <meta property="og:url" content="${escapeHtml(shareUrl)}">
    <meta property="og:type" content="website">
    <meta property="og:site_name" content="SalesRook">
    
    <!-- Twitter Card Meta Tags -->
    <meta name="twitter:card" content="summary_large_image">
    <meta name="twitter:title" content="${escapeHtml(title)}">
    <meta name="twitter:description" content="${escapeHtml(description)}">
    <meta name="twitter:image" content="https://storage.googleapis.com/sr-widget/salesrook-image.jpeg">
    <meta name="twitter:domain" content="${escapeHtml(shareUrl.split('/')[2] || '')}">
    
    <!-- Additional Meta Tags -->
    <meta name="author" content="SalesRook">
    <meta name="robots" content="index, follow">
    <meta http-equiv="Content-Type" content="text/html; charset=utf-8">
    <link rel="canonical" href="${escapeHtml(shareUrl)}">
    
    <!-- Favicon and Icons (from report template) -->
    <link rel="icon" type="image/png" sizes="32x32" href="https://storage.googleapis.com/sr-widget/favicon-32x32.png">
    <link rel="icon" type="image/png" sizes="16x16" href="https://storage.googleapis.com/sr-widget/favicon-16x16.png">
    <link rel="apple-touch-icon" sizes="180x180" href="https://storage.googleapis.com/sr-widget/apple-touch-icon.png">
    <link rel="shortcut icon" href="https://storage.googleapis.com/sr-widget/favicon.ico">
    
    <link href="https://fonts.googleapis.com/css2?family=Alata:wght@400&family=Montserrat:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    
    <!-- SalesRook header/footer styles only - user content theme is preserved -->
    <style>
      :root {
        --sr-primary: #1f1f1f;
        --sr-blue: #3482cf;
        --sr-gold: #f0cf69;
        --sr-purple: #877adb;
      }
      .sr-artifact-container { max-width: 1200px; margin: 0 auto; padding: 0 20px; background: transparent; }
      .sr-artifact-logo-header { text-align: center; padding: 20px 0; border-bottom: 2px solid rgba(255,255,255,0.2); margin-bottom: 20px; }
      .sr-artifact-logo-image { max-height: 60px; width: auto; margin-bottom: 10px; }
      .sr-artifact-header { background: linear-gradient(135deg, var(--sr-blue) 0%, var(--sr-purple) 100%); color: #fff; padding: 40px 0; position: relative; overflow: hidden; }
      .sr-artifact-header::before { content: ''; position: absolute; top: -50%; right: -20%; width: 100%; height: 200%; background: radial-gradient(circle, rgba(240,207,105,.1) 0%, transparent 70%); animation: sr-float 6s ease-in-out infinite; }
      @keyframes sr-float { 0%,100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-20px) rotate(5deg); } }
      .sr-artifact-footer { background: var(--sr-primary); color: #fff; padding: 50px 0; margin-top: 60px; }
      .sr-artifact-footer-content { display: grid; grid-template-columns: 1fr 1fr; gap: 50px; }
      .sr-artifact-talk-section { background: linear-gradient(135deg, var(--sr-blue), var(--sr-purple)); border-radius: 15px; padding: 30px; text-align: center; }
      .sr-artifact-talk-link { display: inline-block; background: #fff; color: var(--sr-blue); padding: 12px 25px; border-radius: 25px; text-decoration: none; font-weight: 600; margin-top: 15px; transition: all .3s ease; }
      .sr-artifact-talk-link:hover { transform: translateY(-2px); box-shadow: 0 5px 15px rgba(0,0,0,0.2); }
      .sr-artifact-main { padding: 40px 0; background: transparent; }
      @media (max-width: 768px) { .sr-artifact-footer-content { grid-template-columns: 1fr; } }
    </style>`;

  const hasHtmlTag = content.toLowerCase().includes('<html');
  const hasHeadTag = content.toLowerCase().includes('<head');
  const hasBodyTag = content.toLowerCase().includes('<body');
  const hasOurHeader = content.includes('sr-artifact-header');
  const hasOurFooter = content.includes('sr-artifact-footer');

  let processedContent = content;

  // Strip conflicting meta/title tags so our tags take precedence
  const stripConflictingHeadTags = (headContent: string): string => {
    return headContent
      .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
      .replace(/<meta\s+[^>]*(?:name|property)=["'](?:description|og:[^"']+|twitter:[^"']+)["'][^>]*>/gi, '')
      .replace(/<meta\s+charset[^>]*>/gi, '')
      .replace(/<meta\s+[^>]*name=["']viewport["'][^>]*>/gi, '')
      .replace(/<link\s+[^>]*rel=["']canonical["'][^>]*>/gi, '')
      .replace(/\n\s*\n/g, '\n')
      .trim();
  };

  const injectMetaTagsForcefully = (html: string): string => {
    const headOpenMatch = html.match(/<head\s*([^>]*)>/i);
    if (!headOpenMatch) return html;
    const headStart = html.indexOf(headOpenMatch[0]) + headOpenMatch[0].length;
    const headEnd = html.toLowerCase().indexOf('</head>');
    if (headEnd === -1) return html;
    const existingHead = html.slice(headStart, headEnd);
    const cleanedHead = stripConflictingHeadTags(existingHead);
    const newHead = metaTags + (cleanedHead ? '\n' + cleanedHead : '');
    return html.slice(0, headStart) + '\n' + newHead + '\n  ' + html.slice(headEnd);
  };

  const wrapWithHeaderFooter = (bodyContent: string): string => {
    if (!includeHeaderFooter) return bodyContent;
    if (hasOurHeader && hasOurFooter) return bodyContent;
    let result = bodyContent;
    if (!hasOurHeader) {
      result = header + '\n' + result;
    }
    if (!hasOurFooter) {
      result = result + '\n' + footer;
    }
    return result;
  };

  const wrapContentInMain = (inner: string): string =>
    `  <main class="sr-artifact-main">\n    <div class="sr-artifact-container">\n${inner}\n    </div>\n  </main>`;

  // Extract body tag and its attributes when user pastes <body ...> without full html
  const extractBodyInfo = (raw: string): { bodyAttrs: string; innerContent: string } => {
    const bodyMatch = raw.match(/<body([^>]*)>([\s\S]*)<\/body>/i);
    if (bodyMatch) {
      return { bodyAttrs: bodyMatch[1].trim(), innerContent: bodyMatch[2].trim() };
    }
    return { bodyAttrs: '', innerContent: raw };
  };

  if (!hasHtmlTag) {
    // No HTML structure - extract body attrs if present to preserve user's background/theme
    const { bodyAttrs, innerContent } = extractBodyInfo(content);
    const bodyTag = bodyAttrs ? `<body ${bodyAttrs}>` : '<body>';
    const bodyContent = includeHeaderFooter
      ? `${header}
${wrapContentInMain(innerContent ? `      ${innerContent}` : '      ')}
${footer}`
      : wrapContentInMain(innerContent ? `      ${innerContent}` : '      ');
    processedContent = `<!DOCTYPE html>
<html lang="en">
<head>
${metaTags}
</head>
${bodyTag}
${bodyContent}
</body>
</html>`;
  } else if (!hasHeadTag) {
    const htmlTagEnd = content.toLowerCase().indexOf('>') + 1;
    processedContent =
      content.slice(0, htmlTagEnd) + `<head>${metaTags}</head>` + content.slice(htmlTagEnd);

    if (hasBodyTag) {
      const bodyStartMatch = processedContent.match(/<body[^>]*>/i);
      if (bodyStartMatch) {
        const bodyStart = processedContent.indexOf(bodyStartMatch[0]) + bodyStartMatch[0].length;
        const bodyEnd = processedContent.toLowerCase().indexOf('</body>');
        const innerBody = processedContent.slice(bodyStart, bodyEnd);
        const wrapped = wrapWithHeaderFooter(innerBody);
        processedContent =
          processedContent.slice(0, bodyStart) + '\n' + wrapped + '\n' + processedContent.slice(bodyEnd);
      }
    }
  } else {
    // Has head tag: forcefully inject our meta tags (strip conflicting ones first)
    processedContent = injectMetaTagsForcefully(processedContent);

    if (hasBodyTag && !hasOurHeader && !hasOurFooter) {
      const bodyStartMatch = processedContent.match(/<body[^>]*>/i);
      if (bodyStartMatch) {
        const bodyStart = processedContent.indexOf(bodyStartMatch[0]) + bodyStartMatch[0].length;
        const bodyEnd = processedContent.toLowerCase().indexOf('</body>');
        const innerBody = processedContent.slice(bodyStart, bodyEnd);
        const wrapped = wrapWithHeaderFooter(innerBody);
        processedContent =
          processedContent.slice(0, bodyStart) + '\n' + wrapped + '\n' + processedContent.slice(bodyEnd);
      }
    }
  }

  // Final fallback: add header/footer if somehow missed (only when includeHeaderFooter is true)
  if (includeHeaderFooter && hasBodyTag && !processedContent.includes('sr-artifact-header')) {
    const bodyStartMatch = processedContent.match(/<body[^>]*>/i);
    if (bodyStartMatch) {
      const bodyStart = processedContent.indexOf(bodyStartMatch[0]) + bodyStartMatch[0].length;
      processedContent = processedContent.slice(0, bodyStart) + '\n' + header + '\n' + processedContent.slice(bodyStart);
    }
  }
  if (includeHeaderFooter && hasBodyTag && !processedContent.includes('sr-artifact-footer')) {
    const bodyEnd = processedContent.toLowerCase().indexOf('</body>');
    if (bodyEnd !== -1) {
      processedContent = processedContent.slice(0, bodyEnd) + '\n' + footer + '\n' + processedContent.slice(bodyEnd);
    }
  }

  return processedContent;
}

function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}
